"use client";

/**
 * BlockNote 기본 TextAlignButton의 커스텀 버전.
 * 테이블 셀에서 가로 정렬 변경 시 ProseMirror 트랜잭션을 직접 사용하여
 * selection이 유지되고 verticalAlignment가 초기화되지 않도록 한다.
 */

import {
  BlockSchema,
  checkBlockHasDefaultProp,
  checkBlockTypeHasDefaultProp,
  InlineContentSchema,
  mapTableCell,
  StyleSchema,
  TableContent,
} from "@blocknote/core";
import { useCallback, useMemo } from "react";
import {
  useComponentsContext,
  useBlockNoteEditor,
  useSelectedBlocks,
} from "@blocknote/react";
import { getSelectedCellPositions } from "../utils/prosemirror-table-utils";

type TextAlignment = "left" | "center" | "right" | "justify";

const icons: Record<TextAlignment, JSX.Element> = {
  left: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
      <path d="M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z" />
    </svg>
  ),
  center: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
      <path d="M7 15v2h10v-2H7zm-4 6h18v-2H3v2zm0-8h18v-2H3v2zm4-6v2h10V7H7zM3 3v2h18V3H3z" />
    </svg>
  ),
  right: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
      <path d="M3 21h18v-2H3v2zm6-4h12v-2H9v2zm-6-4h18v-2H3v2zm6-4h12V7H9v2zM3 3v2h18V3H3z" />
    </svg>
  ),
  justify: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
      <path d="M3 21h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18V7H3v2zM3 3v2h18V3H3z" />
    </svg>
  ),
};

const tooltipMap: Record<TextAlignment, string> = {
  left: "왼쪽 정렬",
  center: "가운데 정렬",
  right: "오른쪽 정렬",
  justify: "양쪽 정렬",
};

export const TextAlignButtonWithVA = (props: {
  textAlignment: TextAlignment;
}) => {
  const Components = useComponentsContext()!;
  const editor = useBlockNoteEditor<
    BlockSchema,
    InlineContentSchema,
    StyleSchema
  >();

  const selectedBlocks = useSelectedBlocks(editor);

  const textAlignment = useMemo(() => {
    const block = selectedBlocks[0];

    if (checkBlockHasDefaultProp("textAlignment", block, editor)) {
      return block.props.textAlignment;
    }
    if (block.type === "table") {
      const cellSelection = editor.tableHandles?.getCellSelection();
      if (!cellSelection) {
        return;
      }
      const allCellsInTable = cellSelection.cells.map(
        ({ row, col }: { row: number; col: number }) =>
          mapTableCell(
            (block.content as TableContent<any, any>).rows[row].cells[col],
          ).props.textAlignment,
      );
      const firstAlignment = allCellsInTable[0];

      if (allCellsInTable.every((alignment: string) => alignment === firstAlignment)) {
        return firstAlignment;
      }
    }

    return;
  }, [editor, selectedBlocks]);

  const setTextAlignment = useCallback(
    (newAlignment: TextAlignment) => {
      editor.focus();

      for (const block of selectedBlocks) {
        if (block.type === "table") {
          const tiptap = editor._tiptapEditor;
          if (!tiptap) continue;

          const positions = getSelectedCellPositions(editor);
          if (positions.length === 0) continue;

          const { state } = tiptap;
          let tr = state.tr;

          for (const pos of positions) {
            const node = tr.doc.nodeAt(pos);
            if (node) {
              tr = tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                textAlignment: newAlignment,
              });
            }
          }

          tiptap.view?.dispatch(tr);
        } else if (checkBlockTypeHasDefaultProp("textAlignment", block.type, editor)) {
          editor.updateBlock(block, {
            props: { textAlignment: newAlignment },
          });
        }
      }
    },
    [editor, selectedBlocks],
  );

  const show = useMemo(() => {
    return !!selectedBlocks.find(
      (block) =>
        "textAlignment" in block.props ||
        (block.type === "table" && block.children),
    );
  }, [selectedBlocks]);

  if (!show || !editor.isEditable) {
    return null;
  }

  return (
    <Components.FormattingToolbar.Button
      className={"bn-button"}
      data-test={`alignText${
        props.textAlignment.slice(0, 1).toUpperCase() +
        props.textAlignment.slice(1)
      }`}
      onClick={() => setTextAlignment(props.textAlignment)}
      isSelected={textAlignment === props.textAlignment}
      label={tooltipMap[props.textAlignment]}
      mainTooltip={tooltipMap[props.textAlignment]}
      icon={icons[props.textAlignment]}
    />
  );
};
